import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  untracked,
  ChangeDetectionStrategy
} from "@angular/core";

declare const $: any;

import { NgClass } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { SessionDataService } from "../../../services/shared/SessionDataService.service";
import { UserControllerService } from "../../../services/server/UserController.service";
import { ProfileService } from "../../../services/shared/ProfileService.service";
import { UserPasswordEdit } from "../../dashboard/users/UserInterface.type";
import { PROFILES } from "../../../../assets/profiles/profiles";
import { User, UserConfig } from "../../../interfaces/UserGlobalInterface.type";
import { BrowserStorageLocalService } from "../../../services/shared/BrowserStorageServices/BrowserStorageLocalService.service";

/**
 * Lets a user update a required password during login.
 * Emits the destination route after the change; exiting asks the parent to close the flow and log out.
 */
@Component({
  selector: "app-change-password-controller",
  imports: [FormsModule, NgClass, TranslateModule],
  templateUrl: "./change-password-controller.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./change-password-controller.scss"
})
export class ChangePasswordController implements OnInit {
  @Output() onPasswordChanged = new EventEmitter<string>();
  @Output() onExit = new EventEmitter<boolean>();

  currentPassword: string = "";
  username: string = "";
  newPassword: string = "";
  confirmPassword: string = "";

  hideCurrent: boolean = true;
  hideNew: boolean = true;
  hideConfirm: boolean = true;

  logoUrl = "";
  defaultImg = "assets/img/logos/arsondata.png";
  showPasswordRequeriments: any;

  constructor(
    private readonly SessionDataService: SessionDataService,
    public userControllerService: UserControllerService,
    private readonly ProfileService: ProfileService,
    private readonly BrowserStorageLocalService: BrowserStorageLocalService
  ) {}

  ngOnInit(): void {
    const sessionData = this.BrowserStorageLocalService.getJsonValue("session");
    const userFromSignal = untracked(() => this.SessionDataService.user());
    this.username = (userFromSignal || sessionData?.user || "").trim();
  }

  canSubmitChange(): boolean {
    return !!(
      this.username?.trim() &&
      this.newPassword &&
      this.confirmPassword &&
      this.newPassword === this.confirmPassword
    );
  }

  submitPasswordChange() {
    const username = this.username.trim();
    let updatePasswordObject: UserPasswordEdit = {
      userName: username,
      newPassword: this.newPassword
    };

    this.userControllerService.updateUserPassword(updatePasswordObject).subscribe(response => {
      if (response["code"] === 0) {
        this.SessionDataService.sendMustChangePassword(false);
        this.updateSessionData();
        this.updateUrl();
      }
    });
  }

  exit() {
    this.onExit.emit(true);
  }

  /** Emits the landing route chosen from the refreshed user profile and enabled services. */
  updateUrl(): void {
    const sessionData = this.BrowserStorageLocalService.getJsonValue("session");
    const userIdFromSignal = untracked(() => this.SessionDataService.userId());
    const userId = Number(userIdFromSignal ?? sessionData?.userId);

    if (!Number.isFinite(userId)) {
      this.onPasswordChanged.emit("/principal");
      return;
    }

    this.userControllerService.getUser(userId).subscribe(response => {
      if (response["code"] === 0) {
        const user: User = response["body"];
        const fallbackProfile = this.ProfileService.getProfile(
          String(user?.profile),
          user?.isTelemedida
        );
        const profile =
          untracked(() => this.SessionDataService.profile()) ||
          sessionData?.profile ||
          (fallbackProfile !== "-" ? fallbackProfile : null);
        const isTlc = user?.isTlc;
        const isTlm = user?.isTelemedida;

        this.rehydrateSession(user, userId, profile, isTlc, isTlm);

        if (profile === PROFILES.ABONADO) {
          this.onPasswordChanged.emit("/abonado/principal");
        } else if (profile === PROFILES.INSTALADOR) {
          this.onPasswordChanged.emit("/instalador/activacion");
        } else if (profile === PROFILES.ADMIN_INSTALLATION) {
          this.onPasswordChanged.emit("/dispositivos/listado/sustituciones");
        } else if (isTlc) {
          this.onPasswordChanged.emit("/telecontrol/alertas-activas");
        } else if (isTlm) {
          this.onPasswordChanged.emit("/telecontrol/telemedida");
        } else {
          this.onPasswordChanged.emit("/principal");
        }
      } else {
        this.onPasswordChanged.emit("/principal");
      }
    });
  }

  /** Synchronizes active session signals and browser storage with the refreshed user. */
  rehydrateSession(
    user: User,
    userId: number,
    profile: string,
    isTlc: boolean,
    isTlm: boolean
  ): void {
    if (this.username) {
      this.SessionDataService.sendUser(this.username);
    }
    this.SessionDataService.sendUserId(user?.id || userId);
    this.SessionDataService.sendRemoteControlActive(!!isTlc);
    this.SessionDataService.sendTelemetryActive(!!isTlm);
    if (profile) {
      this.SessionDataService.sendProfile(profile);
    }

    const sessionData = this.BrowserStorageLocalService.getJsonValue("session") || {};
    sessionData.user = this.username || sessionData.user;
    sessionData.userId = user?.id || userId;
    if (profile) {
      sessionData.profile = profile;
    }
    sessionData.isTlm = !!isTlm;
    this.BrowserStorageLocalService.setJsonValue("session", sessionData);
  }

  /** Clears `mustChangePassword` in stored session data to prevent a password-change redirect loop. */
  updateSessionData(): void {
    let sessionData = this.BrowserStorageLocalService.getJsonValue("session");
    if (sessionData) {
      sessionData.mustChangePassword = false;
      this.BrowserStorageLocalService.setJsonValue("session", sessionData);
    }
  }

  getUserLocalConfig(): UserConfig {
    let configData: UserConfig[] = this.BrowserStorageLocalService.getJsonValue("config");
    return configData?.find(
      (config: UserConfig) => config.user === untracked(() => this.SessionDataService.user())
    );
  }
}
